import React, { useEffect, useRef, useState } from 'react'
import './offer-countdown.css'

export interface OfferCountdownProps {
  /** Offer deadline, unix seconds. */
  expiresAt: number
  /** Line shown before the digits. */
  label?: string
  /** Fired once when the countdown reaches zero (also when it mounts already expired). */
  onExpire?: () => void
  /** 'banner' (default): own soft-green box. 'inline': compact, boxless — for embedding in another card's header. */
  variant?: 'banner' | 'inline'
}

interface TimeParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const partsUntil = (expiresAt: number): TimeParts | null => {
  const totalSeconds = Math.floor(expiresAt - Date.now() / 1000)
  if (totalSeconds <= 0) return null
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

const two = (n: number) => String(n).padStart(2, '0')

/**
 * Quiet ticking deadline banner for personal offers (first used by the plan
 * page after /claim/offer; the taster upsell can reuse it). Renders nothing
 * once the deadline has passed and fires onExpire exactly once so the caller
 * can drop the associated offer state.
 */
const OfferCountdown: React.FC<OfferCountdownProps> = ({
  expiresAt,
  label = 'Your 50% off expires in',
  onExpire,
  variant = 'banner',
}) => {
  const [parts, setParts] = useState<TimeParts | null>(() => partsUntil(expiresAt))
  const expiredNotified = useRef(false)

  useEffect(() => {
    const tick = () => {
      const next = partsUntil(expiresAt)
      setParts(next)
      if (next === null && !expiredNotified.current) {
        expiredNotified.current = true
        onExpire && onExpire()
      }
    }
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [expiresAt])

  if (!parts) return null

  const segments: Array<{ value: string; unit: string }> = [
    { value: two(parts.days), unit: 'Days' },
    { value: two(parts.hours), unit: 'Hours' },
    { value: two(parts.minutes), unit: 'Minutes' },
    { value: two(parts.seconds), unit: 'Seconds' },
  ]

  return (
    <div
      className={variant === 'inline' ? 'offer-countdown offer-countdown--inline' : 'offer-countdown'}
      role='timer'
      aria-live='off'
    >
      {label && <span className='offer-countdown-label'>{label}</span>}
      <div className='offer-countdown-clock'>
        {segments.map((segment, index) => (
          <React.Fragment key={segment.unit}>
            {index > 0 && (
              <span className='offer-countdown-separator' aria-hidden='true'>
                :
              </span>
            )}
            <div className='offer-countdown-segment'>
              <span className='offer-countdown-value'>{segment.value}</span>
              <span className='offer-countdown-unit'>{segment.unit}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export default OfferCountdown
